/**
 * Global editable-text helpers for ArcLog OS.
 * Keyboard + right-click cut/copy/paste/select-all.
 */
import { contextMenuStore, type ContextMenuItem } from "./context-menu";
import { Copy, ClipboardPaste, Scissors, TextSelect } from "lucide-react";

const EDIT_SEL =
  "input, textarea, [contenteditable], [contenteditable=true], [contenteditable=plaintext-only]";

export function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof Element)) return false;
  const node = el as HTMLElement;
  try { if (node.closest(EDIT_SEL)) return true; } catch { /* */ }
  if (node.isContentEditable) return true;
  const tag = node.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") {
    const t = ((node as HTMLInputElement).type || "text").toLowerCase();
    const block = ["button","submit","checkbox","radio","range","file","color","hidden"].indexOf(t) >= 0;
    return !block && !(node as HTMLInputElement).disabled;
  }
  return false;
}

export function hasTextSelection(): boolean {
  try {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && String(sel.toString() || "").length > 0) return true;
  } catch { /* */ }
  const ae = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA")) {
    try {
      const s = ae.selectionStart ?? 0;
      const e = ae.selectionEnd ?? 0;
      return e > s;
    } catch { /* */ }
  }
  return false;
}

/**
 * Editors that implement clipboard / select-all / undo themselves, on their own
 * DOM node, rather than relying on the browser's default action.
 *
 * CodeMirror is one: its history lives in JS and is driven by a keymap handler
 * bound to `.cm-editor`. The capture-phase guard in installGlobalTextEditing
 * calls stopImmediatePropagation(), which in the *capture* phase halts the
 * event before it ever reaches that handler — so ⌘Z/⌘⇧Z/⌘Y did nothing in any
 * code editor. Native inputs were unaffected, because stopping propagation
 * doesn't cancel the browser's default action; CodeMirror has no default action
 * to fall back on.
 */
export function isSelfManagedEditor(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return !!el.closest(".cm-editor");
}

export function isBrowserEditHotkey(e: KeyboardEvent): boolean {
  const mod = e.ctrlKey || e.metaKey;
  if (!mod || e.altKey) return false;
  const k = e.key.toLowerCase();
  if (["c","x","v","a","z","y"].indexOf(k) < 0) return false;
  if (isEditableTarget(e.target)) return true;
  if ((k === "c" || k === "a") && hasTextSelection()) return true;
  return false;
}

function getInputSelection(el: HTMLElement | null): string {
  if (!el) return "";
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    try {
      const s = el.selectionStart ?? 0;
      const e = el.selectionEnd ?? 0;
      return el.value.slice(s, e);
    } catch { return ""; }
  }
  return "";
}

function replaceInputSelection(el: HTMLElement, replacement: string) {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.value = el.value.slice(0, start) + replacement + el.value.slice(end);
    const caret = start + replacement.length;
    el.selectionStart = el.selectionEnd = caret;
    el.dispatchEvent(new Event("input", { bubbles: true }));
  } else if (el.isContentEditable) {
    document.execCommand("insertText", false, replacement);
  }
}

async function runEditCommand(cmd: "cut" | "copy" | "paste" | "selectAll"): Promise<void> {
  const ae = document.activeElement as HTMLElement | null;
  try {
    if (cmd === "paste") {
      if (ae && isEditableTarget(ae) && navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        replaceInputSelection(ae, text);
        return;
      }
    }
    if (cmd === "copy" || cmd === "cut") {
      const ok = document.execCommand(cmd);
      if (!ok && navigator.clipboard && navigator.clipboard.writeText) {
        const sel = window.getSelection();
        const t = (sel && sel.toString()) || getInputSelection(ae);
        if (t) await navigator.clipboard.writeText(t);
        if (cmd === "cut" && ae && isEditableTarget(ae)) replaceInputSelection(ae, "");
      }
      return;
    }
    document.execCommand(cmd);
  } catch { /* */ }
}

export function openTextEditContextMenu(clientX: number, clientY: number, target?: EventTarget | null): boolean {
  const el = (target as EventTarget | null) || document.activeElement;
  const editable = isEditableTarget(el);
  const selected = hasTextSelection();
  const items: ContextMenuItem[] = [];
  if (editable) {
    items.push(
      { id: "cut", label: "Cut", icon: Scissors, shortcut: "Ctrl+X", action: () => { void runEditCommand("cut"); } },
      { id: "copy", label: "Copy", icon: Copy, shortcut: "Ctrl+C", action: () => { void runEditCommand("copy"); } },
      { id: "paste", label: "Paste", icon: ClipboardPaste, shortcut: "Ctrl+V", action: () => { void runEditCommand("paste"); } },
      { id: "s1", separator: true },
      { id: "selectAll", label: "Select All", icon: TextSelect, shortcut: "Ctrl+A", action: () => { void runEditCommand("selectAll"); } }
    );
  } else if (selected) {
    items.push({ id: "copy", label: "Copy", icon: Copy, shortcut: "Ctrl+C", action: () => { void runEditCommand("copy"); } });
  }
  if (!items.length) return false;
  contextMenuStore.open(clientX, clientY, { kind: "app", detail: "text-edit" }, items);
  return true;
}

export function installGlobalTextEditing() {
  if (typeof window === "undefined") return;
  const w = window as Window & { __arclogTextEditInstalled?: boolean };
  if (w.__arclogTextEditInstalled) return;
  w.__arclogTextEditInstalled = true;
  window.addEventListener(
    "keydown",
    (e) => {
      if (!isBrowserEditHotkey(e)) return;
      // CodeMirror handles these itself, and better (multi-cursor aware).
      // Stopping the event here would mean it never reaches its keymap.
      if (isSelfManagedEditor(e.target)) return;
      // Prevent other capture handlers from stealing C/X/V/A
      e.stopImmediatePropagation();
    },
    true
  );
  window.addEventListener(
    "contextmenu",
    (e) => {
      if (isEditableTarget(e.target) || hasTextSelection()) {
        e.preventDefault();
        e.stopPropagation();
        openTextEditContextMenu(e.clientX, e.clientY, e.target);
      }
    },
    true
  );
}
